import { useState } from "react";

import ClockStopwatch from "@uui/ClockStopwatch";
import { isEmpty } from "lodash/fp";

import { Skeleton, Stack, Typography } from "@mui/material";

import EmptyContent from "@app/analytics/WorkspaceOverview/components/EmptyContent";
import ErrorContent from "@app/analytics/WorkspaceOverview/components/ErrorContent";
import useTimeKeeper from "@app/analytics/WorkspaceOverview/TimeKeeper/useTimeKeeper";

import MetricToggle from "./MetricToggle";
import PolicyBar from "./PolicyBar";
import TeamsList from "./TeamsList";
import { TimeKeeperMetric, TimeKeeperMetricType, ToggleFn } from "./types";
import { getPropertyToSortBy } from "./utils";
import ModuleHeader from "../components/ModuleHeader";
import ModuleTitle from "../components/ModuleTitle";
import {
  WorkspaceModule,
  WorkspaceModuleBody,
  WorkspaceModuleTab,
  WorkspaceModuleTabs,
} from "../components/styles";

const getSubtitle = (
  currentTab: TimeKeeperMetric,
  metricType: TimeKeeperMetricType
) => {
  if (currentTab === "lateStart") {
    return `${
      metricType === "time" ? "Average late time for" : "Percent of"
    } meetings that start after their scheduled time.`;
  }

  return `${
    metricType === "time" ? "Average overage time for" : "Percent of"
  } meetings that run over their scheduled time.`;
};

const LoadingState = () => {
  return (
    <Stack
      spacing={1}
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{ width: "100%", height: "100%" }}
    >
      <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
        <Skeleton
          variant="rectangular"
          sx={{ width: "50%", height: "200px", borderRadius: "8px" }}
        />
        <Skeleton
          variant="rectangular"
          sx={{ width: "50%", height: "200px", borderRadius: "8px" }}
        />
      </Stack>
    </Stack>
  );
};

const TimeKeeper = () => {
  const [metric, setMetric] = useState<TimeKeeperMetricType>("percent");
  const [currentTab, setCurrentTab] = useState<TimeKeeperMetric>("lateStart");

  const { isInitialLoading, isError, tableData, workspaceAverages, refetch } =
    useTimeKeeper();

  const propertyToSortBy = getPropertyToSortBy(currentTab, metric);
  const value = Math.round(workspaceAverages[propertyToSortBy]);

  const handleMetricChange: ToggleFn = (event, value) => {
    if (!value) return;
    setMetric(value as TimeKeeperMetricType);
  };

  const handleTabChange: ToggleFn = (event, value) => {
    setCurrentTab(value as TimeKeeperMetric);
  };

  const averageIsZero = workspaceAverages[propertyToSortBy] === 0;
  const showEmptyContent =
    !isInitialLoading && (isEmpty(tableData) || averageIsZero);

  return (
    <WorkspaceModule>
      <ModuleHeader>
        <ModuleTitle icon={ClockStopwatch}>Time Keeper</ModuleTitle>
      </ModuleHeader>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: "100%", pr: 2 }}
      >
        <WorkspaceModuleTabs value={currentTab} onChange={handleTabChange}>
          <WorkspaceModuleTab label="Late Start" value="lateStart" />
          <WorkspaceModuleTab label="Meeting Overage" value="overTime" />
        </WorkspaceModuleTabs>
        <MetricToggle metric={metric} onToggle={handleMetricChange} />
      </Stack>
      <WorkspaceModuleBody>
        {isError ? (
          <ErrorContent onRetry={refetch} sx={{ height: "240px" }} />
        ) : showEmptyContent ? (
          <EmptyContent />
        ) : (
          <>
            <Typography
              variant="bodySecondaryReg"
              sx={{ color: "shades.neutral.600", mb: 1.5 }}
            >
              {getSubtitle(currentTab, metric)}
            </Typography>
            {isInitialLoading ? (
              <LoadingState />
            ) : (
              <Stack direction="row" sx={{ width: "100%" }} spacing={2}>
                <Stack
                  sx={{ width: "100%" }}
                  spacing={1}
                  justifyContent="center"
                >
                  <Stack direction="row" alignItems="center">
                    <Typography
                      variant="statPrimary"
                      sx={{ color: "shades.primary.500" }}
                    >
                      {value}
                      {metric === "percent" ? "%" : " min"}
                    </Typography>
                  </Stack>
                  <PolicyBar
                    metric={metric}
                    currentTab={currentTab}
                    teams={tableData}
                    workspaceAverages={workspaceAverages}
                  />
                </Stack>
                <TeamsList
                  workspaceAverages={workspaceAverages}
                  teams={tableData}
                  metric={metric}
                  currentTab={currentTab}
                />
              </Stack>
            )}
          </>
        )}
      </WorkspaceModuleBody>
    </WorkspaceModule>
  );
};

export default TimeKeeper;
